import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import type { HierarchicalFilterOption } from '../types';
import { useT } from '../i18n';

/* ── styled components (matching SearchableSelect visuals) ── */

// Container is the shared `.dropdown-labeled` pattern: column flex with the
// label sitting above the trigger as a block element. Differs from the
// shared SCSS in that we render the label as a real <label> rather than via
// `::before { content: attr(data-label) }`, so screen readers pick it up.
const Container = styled.div<{ isOpen: boolean }>`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-1);
  width: 100%;
  /* As a grid item in FilterPanel's FilterGrid, the default min-width:auto
     lets the track grow to fit a long selected label, blowing out the pill.
     min-width:0 keeps the pill at its track width and lets the trigger's
     DisplayText ellipsis clip instead. The dropdown stays wide via its own
     min-width, so suggestions don't get squeezed into a stacked column. */
  min-width: 0;
  /* Local to FilterCard's stacking context — open select overlaps siblings. */
  z-index: ${props => props.isOpen ? 3 : 2};
`;

const Label = styled.label`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.2;
  color: var(--color-text-default);
  /* Scaled off the widget base, not em, so the label's left edge lines up with
     the trigger text: the button pads 0.8em at 16px (= 12.8px), so a plain
     0.8em here at 13px would under-indent and the label would sit too far
     left. */
  padding: 0 ${scaled(12.8)};
`;

// Replicates the shared `.dropdown` styling: pill shape, sans font,
// chevron baked into background-image (filled triangle, matches the SCSS
// data-URL chevron). Background is white to stand out against the beige
// page bg. Border is barely-there at rest, dark when open.
const SelectButton = styled.button<{ isOpen: boolean; hasValue: boolean }>`
  font-family: var(--sans-stack);
  font-size: ${scaled(16)};
  line-height: 1.2;
  padding: 0.3em 2.6em 0.2em 0.8em;
  border-radius: 999px;
  border: 1px solid ${props => props.isOpen ? 'var(--color-text-default)' : 'rgba(0, 0, 0, 0.1)'};
  background-color: transparent;
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24' fill='%235f6368'%3E%3Cpath d='M480-360 280-560h400L480-360Z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.1em center;
  background-size: 2em;
  color: ${props => props.hasValue ? 'var(--color-text-default)' : 'var(--color-text-muted)'};
  cursor: pointer;
  outline: none;
  width: 100%;
  text-align: left;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
`;

const ButtonChevron = styled.span<{ isOpen: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease;
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
`;

// Clear-X sits to the left of the chevron and short-circuits the
// selection without opening the dropdown — mirrors the same affordance
// in the budget widget so users can drop a single filter without
// invoking "Filter zurücksetzen".
const InlineClearButton = styled.button`
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: ${theme.colors.background};
  border-radius: 50%;
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s;
  padding: 0;

  &:hover {
    background: ${theme.colors.error};
    color: white;
    transform: translateY(-50%) scale(1.1);
  }
`;

const DisplayText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 24px;
  font-weight: 450;
`;

// Panel matches the website screenshot: clean white card, subtle shadow,
// 8px radius, no heavy border. Search input has the magnifier on the
// RIGHT (per the website), inline checkbox + label + (count) layout, thin
// row separators, no fancy hover backgrounds.
const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 320px;
  width: 100%;
  background: #ffffff;
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-m);
  /* Local to the select Container's stacking context. */
  z-index: 9;
`;

// Search bar wrapper provides padding around the bordered-pill input. The
// input itself has its own border (matches website where the search bar is
// a self-contained pill, not a flush flat field with a section divider).
const SearchInputWrapper = styled.div`
  padding: var(--space-3) var(--space-4);
  position: relative;
  box-sizing: border-box;
`;

// Magnifier sits on the RIGHT (inside the pill). Offset is pixel-based so
// the icon stays inside the pill regardless of the host page's root
// font-size (the earlier `0.8em` would scale and could overflow).
const SearchInputIcon = styled.span`
  position: absolute;
  right: calc(var(--space-4) + 12px);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  padding: 0.5em 2.4em 0.5em 0.8em;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  background: transparent;
  color: var(--color-text-default);
  outline: none;
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  &::placeholder { color: var(--color-text-muted); }
  &:focus {
    border-color: var(--color-text-default);
  }
`;

const OptionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: var(--color-bg-muted); border-radius: 3px; }
`;

// `disabled` is plumbed for visual parity with budget's CascadingSelect.
// Investments has no availability data feeding it today, so no caller
// passes `disabled={true}` yet — but the prop is part of the contract so
// future wiring (per-node reachability counts) can light up the dim/
// not-allowed state without another styling pass.
const OptionItem = styled.li<{ isSelected: boolean; inherited?: boolean; disabled?: boolean }>`
  padding: var(--space-3) var(--space-4);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.3;
  color: var(--color-text-default);
  border-bottom: 1px solid var(--color-bg-muted);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  opacity: ${props => props.disabled ? 0.35 : props.inherited ? 0.6 : 1};
  position: relative;

  &:last-child { border-bottom: none; }
  &:hover { background: ${props => props.disabled ? 'transparent' : 'var(--color-bg-surface)'}; }
`;

// Wrapper expands the tap target to ~28×28 without growing the visual
// square, so touch users can hit the checkbox reliably without
// accidentally triggering the surrounding row's drill-in click.
const CheckboxHit = styled.span`
  flex-shrink: 0;
  padding: 5px;
  margin: -5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  &:hover { background: rgba(0, 0, 0, 0.06); }
`;

// Sized close to the 13px suggestion text so it reads as part of the
// row. `indeterminate` (some-but-not-all descendants selected) fills like a
// checked box but shows a dash instead of a tick.
const Checkbox = styled.span<{ checked: boolean; inherited?: boolean; indeterminate?: boolean }>`
  width: 14px;
  height: 14px;
  border: 1.5px solid var(--color-text-default);
  border-radius: 2px;
  background: ${props => props.checked || props.indeterminate ? 'var(--color-text-default)' : props.inherited ? 'var(--color-bg-muted)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

// Label and count sit inline (Bergbau und Gewinnung … (7)). Count is muted
// and just a plain span, no pill background.
const OptionLabel = styled.span`
  flex: 1;
  line-height: 1.3;
  word-break: break-word;
  min-width: 0;
`;

const OptionCount = styled.span`
  color: var(--color-text-muted);
  margin-left: 0.4em;
`;

const ItemChevron = styled.span`
  flex-shrink: 0;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
`;

const NoResults = styled.div`
  padding: var(--space-6);
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
`;

const SearchPath = styled.span`
  font-size: ${scaled(12)};
  color: var(--color-text-muted);
  margin-right: 4px;
`;

/* ── Back row (shown at top of list when drilled into a child level) ── */

const BackRow = styled.li`
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.3;
  color: var(--color-text-muted);
  border-bottom: 1px solid var(--color-bg-muted);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  &:hover { background: var(--color-bg-surface); }
`;

const BackArrow = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-muted);
  flex-shrink: 0;
`;

const BackLabel = styled.span`
  flex: 1;
  min-width: 0;
  word-break: break-word;
  color: var(--color-text-default);
  font-weight: 450;
`;

/* ── SVGs ── */

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronDownSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6,9 12,15 18,9" />
  </svg>
);

const ChevronRightSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9,6 15,12 9,18" />
  </svg>
);

const BackArrowSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12,19 5,12 12,5" />
  </svg>
);

const CheckSvg = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

// Dash for the indeterminate (partial-selection) state.
const MinusSvg = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ClearXSvg = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ── Helpers ── */

function flattenTree(
  nodes: HierarchicalFilterOption[],
  parentPath: string[] = [],
): { node: HierarchicalFilterOption; path: string[] }[] {
  const result: { node: HierarchicalFilterOption; path: string[] }[] = [];
  for (const n of nodes) {
    result.push({ node: n, path: parentPath });
    if (n.children) {
      result.push(...flattenTree(n.children, [...parentPath, n.label]));
    }
  }
  return result;
}

function collectChildValues(node: HierarchicalFilterOption): string[] {
  const values: string[] = [];
  if (node.children) {
    for (const child of node.children) {
      values.push(child.value);
      values.push(...collectChildValues(child));
    }
  }
  return values;
}

function findLabelForValue(tree: HierarchicalFilterOption[], value: string): string | null {
  for (const node of tree) {
    if (node.value === value) return node.label;
    if (node.children) {
      const found = findLabelForValue(node.children, value);
      if (found) return found;
    }
  }
  return null;
}

/* ── Main component ── */

interface CascadingSelectProps {
  tree: HierarchicalFilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  label: string;
  /** Display form of a node label (e.g. industryLabel); values stay the keys.
      The search matches the displayed label and the raw one. */
  formatLabel?: (label: string) => string;
}

const identity = (label: string) => label;

export function CascadingSelect({ tree, value, onChange, placeholder, label, formatLabel = identity }: CascadingSelectProps) {
  const m = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Drill-in navigation: each entry is an ancestor the user has clicked
  // into. Empty array = showing L1. The last entry's `children` is the
  // list currently rendered.
  const [path, setPath] = useState<HierarchicalFilterOption[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  // Compute which values are inherited (parent is selected)
  const inheritedSet = useMemo(() => {
    const inherited = new Set<string>();
    for (const node of tree) {
      if (selectedSet.has(node.value) && node.children) {
        for (const child of node.children) {
          inherited.add(child.value);
          if (child.children) {
            for (const gc of child.children) inherited.add(gc.value);
          }
        }
      } else if (node.children) {
        for (const child of node.children) {
          if (selectedSet.has(child.value) && child.children) {
            for (const gc of child.children) inherited.add(gc.value);
          }
        }
      }
    }
    return inherited;
  }, [tree, selectedSet]);

  // Nodes in a partial (indeterminate) state: not themselves selected or
  // inherited, but with at least one descendant explicitly selected. Drives
  // the dash glyph so a parent signals "some children chosen" at a glance.
  const indeterminateSet = useMemo(() => {
    const result = new Set<string>();
    const hasSelectedDescendant = (node: HierarchicalFilterOption): boolean => {
      if (!node.children) return false;
      return node.children.some(
        child => selectedSet.has(child.value) || hasSelectedDescendant(child),
      );
    };
    const walk = (nodes: HierarchicalFilterOption[]) => {
      for (const node of nodes) {
        if (!selectedSet.has(node.value) && !inheritedSet.has(node.value) && hasSelectedDescendant(node)) {
          result.add(node.value);
        }
        if (node.children) walk(node.children);
      }
    };
    walk(tree);
    return result;
  }, [tree, selectedSet, inheritedSet]);

  // Flat search results
  const flatItems = useMemo(() => flattenTree(tree), [tree]);
  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    const lower = searchTerm.toLowerCase();
    return flatItems.filter(({ node }) =>
      formatLabel(node.label).toLowerCase().includes(lower) || node.label.toLowerCase().includes(lower));
  }, [flatItems, searchTerm, formatLabel]);

  // Display text
  const displayValue = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const lbl = findLabelForValue(tree, value[0]!);
      return formatLabel(lbl || value[0]!);
    }
    return m.select.selected(value.length);
  }, [value, tree, placeholder, formatLabel, m]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setPath([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const toggleSelect = useCallback((node: HierarchicalFilterOption) => {
    const isSelected = selectedSet.has(node.value);
    if (isSelected) {
      // Deselect: remove this value and any child values
      const toRemove = new Set([node.value, ...collectChildValues(node)]);
      onChange(value.filter(v => !toRemove.has(v)));
    } else {
      // Select: add this value, remove any child values (parent subsumes them)
      const childValues = new Set(collectChildValues(node));
      const filtered = value.filter(v => !childValues.has(v));
      // Also check if a parent is already selected (inherited) — if so, we need to
      // do exclusive deselection: this is selecting something already inherited, which is a no-op visually
      // Actually if inherited, clicking should deselect — remove parent and add siblings
      if (inheritedSet.has(node.value)) {
        // Find the parent that's selected and do exclusive deselection
        const newValues = handleExclusiveDeselect(node.value);
        if (newValues) { onChange(newValues); return; }
      }
      onChange([...filtered, node.value]);
    }
  }, [selectedSet, inheritedSet, value, onChange]);

  // Handle deselecting a child when its parent is selected
  const handleExclusiveDeselect = useCallback((targetValue: string): string[] | null => {
    for (const l1 of tree) {
      if (selectedSet.has(l1.value) && l1.children) {
        // Check if target is a direct L2 child
        const l2Match = l1.children.find(c => c.value === targetValue);
        if (l2Match) {
          const siblings = l1.children.filter(c => c.value !== targetValue).map(c => c.value);
          return [...value.filter(v => v !== l1.value), ...siblings];
        }
        // Check if target is an L3 grandchild
        for (const l2 of l1.children) {
          if (l2.children) {
            const l3Match = l2.children.find(c => c.value === targetValue);
            if (l3Match) {
              // Remove L1, add all other L2 siblings + all L3 siblings of the matched L2
              const otherL2s = l1.children.filter(c => c.value !== l2.value).map(c => c.value);
              const l3Siblings = l2.children.filter(c => c.value !== targetValue).map(c => c.value);
              return [...value.filter(v => v !== l1.value), ...otherL2s, ...l3Siblings];
            }
          }
        }
      }
      // Check L2 parent selected
      if (l1.children) {
        for (const l2 of l1.children) {
          if (selectedSet.has(l2.value) && l2.children) {
            const l3Match = l2.children.find(c => c.value === targetValue);
            if (l3Match) {
              const siblings = l2.children.filter(c => c.value !== targetValue).map(c => c.value);
              return [...value.filter(v => v !== l2.value), ...siblings];
            }
          }
        }
      }
    }
    return null;
  }, [tree, selectedSet, value]);

  // Items shown in the dropdown right now: top-level when path is empty,
  // otherwise the children of the deepest ancestor the user has drilled
  // into.
  const currentParent = path.length === 0 ? null : path[path.length - 1]!;
  const currentItems = currentParent?.children ?? (path.length === 0 ? tree : []);

  const isSearching = searchTerm.length > 0;

  return (
    <Container ref={containerRef} isOpen={isOpen}>
      <Label>{label}</Label>
      <SelectButton
        type="button"
        isOpen={isOpen}
        hasValue={value.length > 0}
        onClick={() => { setIsOpen(!isOpen); if (isOpen) { setSearchTerm(''); setPath([]); } }}
      >
        <DisplayText>{displayValue}</DisplayText>
        {value.length > 0 && (
          <InlineClearButton
            type="button"
            onClick={e => { e.stopPropagation(); onChange([]); }}
            title={m.common.removeFilter}
            aria-label={m.common.removeFilter}
          >
            <ClearXSvg />
          </InlineClearButton>
        )}
        {/* Chevron is rendered via SelectButton's background-image — see styled definition above. */}
      </SelectButton>

      {isOpen && (
        <Dropdown>
          <SearchInputWrapper>
            <SearchInputIcon><SearchIcon /></SearchInputIcon>
            <SearchInput
              ref={searchInputRef}
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); }}
              placeholder={m.select.search}
              onClick={e => e.stopPropagation()}
            />
          </SearchInputWrapper>

          {isSearching ? (
            /* ── Search mode: flat list ── */
            searchResults.length === 0 ? (
              <NoResults>{m.select.noResults}</NoResults>
            ) : (
              <OptionsList>
                {searchResults.map(({ node, path }) => {
                  const isSelected = selectedSet.has(node.value);
                  const isInherited = inheritedSet.has(node.value);
                  const isIndeterminate = indeterminateSet.has(node.value);
                  return (
                    <OptionItem
                      key={node.value}
                      isSelected={isSelected}
                      inherited={isInherited && !isSelected}
                      onClick={e => { e.stopPropagation(); toggleSelect(node); }}
                    >
                      <Checkbox checked={isSelected} inherited={isInherited && !isSelected} indeterminate={isIndeterminate}>
                        {(isSelected || isInherited) ? <CheckSvg /> : isIndeterminate ? <MinusSvg /> : null}
                      </Checkbox>
                      <OptionLabel>
                        {path.length > 0 && <SearchPath>{path.map(formatLabel).join(' > ')} &gt; </SearchPath>}
                        {formatLabel(node.label)}
                        <OptionCount>({node.count})</OptionCount>
                      </OptionLabel>
                    </OptionItem>
                  );
                })}
              </OptionsList>
            )
          ) : (
            /* ── Normal mode: drill-in panel ── */
            <OptionsList>
              {currentParent && (
                <BackRow onClick={e => { e.stopPropagation(); setPath(p => p.slice(0, -1)); }}>
                  <BackArrow><BackArrowSvg /></BackArrow>
                  <BackLabel>{formatLabel(currentParent.label)}</BackLabel>
                </BackRow>
              )}
              {currentItems.map(item => {
                const isSelected = selectedSet.has(item.value);
                const isInherited = inheritedSet.has(item.value);
                const isIndeterminate = indeterminateSet.has(item.value);
                const hasChildren = !!item.children && item.children.length > 0;
                const drillOrToggle = () => {
                  if (hasChildren) setPath(p => [...p, item]);
                  else toggleSelect(item);
                };
                return (
                  <OptionItem
                    key={item.value}
                    isSelected={isSelected}
                    inherited={isInherited && !isSelected}
                    onClick={e => { e.stopPropagation(); drillOrToggle(); }}
                  >
                    <CheckboxHit
                      onClick={e => { e.stopPropagation(); toggleSelect(item); }}
                      role="checkbox"
                      aria-checked={isSelected ? 'true' : isIndeterminate ? 'mixed' : 'false'}
                    >
                      <Checkbox checked={isSelected} inherited={isInherited && !isSelected} indeterminate={isIndeterminate}>
                        {(isSelected || isInherited) ? <CheckSvg /> : isIndeterminate ? <MinusSvg /> : null}
                      </Checkbox>
                    </CheckboxHit>
                    <OptionLabel>
                      {formatLabel(item.label)}
                      <OptionCount>({item.count})</OptionCount>
                    </OptionLabel>
                    {hasChildren && <ItemChevron><ChevronRightSvg /></ItemChevron>}
                  </OptionItem>
                );
              })}
            </OptionsList>
          )}
        </Dropdown>
      )}
    </Container>
  );
}
